import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../../../contexts/AuthContext"
import ProfileSecurityTab from "./ProfileSecurityTab"
import MembersTab from "./MembersTab"
import FirmTab from "./FirmTab"
import PlatformTab from "./PlatformTab"

type TabKey = 'profile' | 'members' | 'firm' | 'platform'

export default function Settings() {
  const { t } = useTranslation()
  // Which tabs a role sees (Task 17): everyone gets their own profile and
  // security; canManageFirm (firm admin or super admin) also gets Membri;
  // the firm admin gets Studio (their own letterhead); the super admin gets
  // Piattaforma (every studio, the shared prompts and maintenance) instead.
  const { isSuperAdmin, isFirmAdmin, canManageFirm } = useAuth()
  const [activeTab, setActiveTab] = useState<TabKey>('profile')

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'profile', label: t('settings.tabs.profile') },
    ...(canManageFirm ? [{ key: 'members' as TabKey, label: t('settings.tabs.members') }] : []),
    ...(isFirmAdmin ? [{ key: 'firm' as TabKey, label: t('settings.tabs.firm') }] : []),
    ...(isSuperAdmin ? [{ key: 'platform' as TabKey, label: t('settings.tabs.platform') }] : []),
  ]

  const tabClass = (tab: TabKey) => `
    py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
    ${
      activeTab === tab
        ? 'border-primary-500 text-primary-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }
  `

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-lg rounded-2xl p-6">
        <h1 className="text-3xl font-bold mb-6">{t('settings.title')}</h1>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={tabClass(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content: only the active one is mounted */}
        {activeTab === 'profile' && <ProfileSecurityTab />}
        {activeTab === 'members' && canManageFirm && <MembersTab />}
        {activeTab === 'firm' && isFirmAdmin && <FirmTab />}
        {activeTab === 'platform' && isSuperAdmin && <PlatformTab />}
      </div>
    </div>
  )
}
